import { msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import 'reactflow/dist/style.css';

import type { StructureEdge, StructureEdgeData, StructureNode, StructureNodeData, StructureNodeType } from '@tf/shared';
import { getDagreLayout, getEdgeParams } from '@tf/shared';
import { ActionIcon, Badge, Box, Button, EntityBadge, Flex, Icon, Menu, Stack, TFText, useClickOutside } from '@tf/ui';
import {
	Background,
	BackgroundVariant,
	type Edge,
	EdgeLabelRenderer,
	type EdgeProps,
	getBezierPath,
	Handle,
	type NodeProps,
	Position,
	ReactFlow,
	type ReactFlowInstance,
	ReactFlowProvider,
	useEdgesState,
	useNodesState,
	useStore,
} from 'reactflow';

import { useApplication, useApplicationMainReview } from '@/components/application';
import { isApplicantStructureHidden } from '@/components/application/hideApplicantKindConnections';
import { useGetConnectionLabels, useGetEntityLabel } from '@/components/shared/connectionLabels';
import { getNodeEditContext } from '@/components/structure/getNodeEditContext';
import { useModalsController } from '@/components/structure/ModalsControllerContext';
import { mergeConnectedEntitiesForms } from '@/components/structure/mergeConnectedEntitiesForms';
import { useStructureEntities } from '@/components/structure/useStructureEntities';
import { useDefinitions } from '@/core/providers';

interface AccountStructure {
	nodes: StructureNode[];
	edges: StructureEdge[];
}

interface Props {
	accountStructure: AccountStructure;
}

export const AccountStructure: React.FC<Props> = ({ accountStructure }) => {
	// we need this dirty hack to avoid unnecessary rerenders due to modal context changes
	const stringifiedtructure = JSON.stringify(accountStructure);
	// biome-ignore lint/correctness/useExhaustiveDependencies: the structure object identity changes on every render
	const memoizedAccountStructure = useMemo(() => accountStructure, [stringifiedtructure]);

	return (
		<ReactFlowProvider>
			<Structure accountStructure={memoizedAccountStructure} />
		</ReactFlowProvider>
	);
};

const Structure: React.FC<Props> = ({ accountStructure }) => {
	const [nodes, setNodes, onNodesChange] = useNodesState<StructureNodeData>([]);
	const [edges, setEdges, onEdgesChange] = useEdgesState<StructureEdgeData>([]);

	const { connectionsMenu } = useModalsController() ?? {};

	const rfInstance = useRef<ReactFlowInstance | undefined>(undefined);

	const restoreInitialLayout = () => {
		const { nodes: initialNodes, edges: initialEdges } = accountStructure;
		const layout = getDagreLayout(initialNodes, initialEdges, 'TB', { width: 300, height: 150 });
		setNodes(layout.nodes);
		setEdges(layout.edges);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: restoreInitialLayout is a new function on every render
	useEffect(() => {
		restoreInitialLayout();
	}, [accountStructure, nodes.length]);

	return (
		<ReactFlow
			nodesDraggable={!connectionsMenu?.isOpen}
			zoomOnScroll={!connectionsMenu?.isOpen}
			panOnDrag={!connectionsMenu?.isOpen}
			onPaneClick={() => connectionsMenu?.close?.()}
			onNodeClick={() => connectionsMenu?.close?.()}
			nodes={nodes}
			edges={edges}
			nodeTypes={nodeTypes}
			edgeTypes={edgeTypes}
			onInit={(reactFlowInstance) => (rfInstance.current = reactFlowInstance)}
			onNodesChange={onNodesChange}
			onEdgesChange={onEdgesChange}
			proOptions={{ hideAttribution: true }}
			minZoom={0.1}
			fitView
			fitViewOptions={{ padding: 0.2, maxZoom: 0.85 }}
		>
			<Background style={{ backgroundColor: '#F9FAFB' }} color="black" variant={BackgroundVariant.Dots} />
		</ReactFlow>
	);
};

export const EntityNode: React.FC<NodeProps<StructureNodeData>> = ({ data, type }) => {
	const { _ } = useLingui();
	const isIndividual = type === 'person';
	const { addConnectionModal, formModal, connectionsMenu } = useModalsController() ?? {};
	const { applicationConnectionKind = 'APPLICANT' } = useParams<'applicationConnectionKind'>();

	const isEditable = addConnectionModal || formModal;
	const connectionsMenuRef = useClickOutside(() => connectionsMenu?.close?.());
	const getConnectionLabels = useGetConnectionLabels(data.kind);

	const mainReview = useApplicationMainReview();
	const application = useApplication();
	const { defs } = useDefinitions();
	const entities = useStructureEntities(applicationConnectionKind);
	const entitiesWithConnectedForms = mergeConnectedEntitiesForms(entities);

	const entity = entitiesWithConnectedForms.find((e) => e.entityId === data.graphId);
	const statuses = entity?.forms.map((f) => f.formStatus.structure);
	const hasErrors = statuses?.some((s) => s === 'PENDING_FILL' || s === 'VALIDATION_ERRORS');
	const isMainEntityOrAccount = data.graphId === mainReview.graphNodeId || data.meta.connectionKind === 'ACCOUNT';
	const editContext = getNodeEditContext({
		stepConnectionKind: applicationConnectionKind,
		accountId: data.meta.accountId,
		entityId: data.graphId,
		parentEntityId: data.parentEntityId,
		isAccount: data.meta.connectionKind === 'ACCOUNT',
		isMainEntity: data.graphId === mainReview.graphNodeId,
	});

	const applicantKind = mainReview.listEntities.find((e) => e.graphNodeId === mainReview.graphNodeId)?.entityKind;
	const isApplicantStructureLocked =
		data.graphId === mainReview.graphNodeId &&
		isApplicantStructureHidden(
			defs.accounts[application.account.entityKind]?.connection_filters,
			mainReview.connectionKind,
			applicantKind,
		);

	const connectionOptions = (
		entity?.connections.map(({ connectionKind, isUnique, connectedEntities }) => {
			const connectionLabel = getConnectionLabels(connectionKind)?.singular;
			return {
				label: _(
					msg({
						id: 'structure.accountStructure.entityNode.connectionOption',
						message: `Add ${connectionLabel}`,
					}),
				),
				kind: connectionKind,
				isUnique,
				hasConnections: connectedEntities.length > 0,
			};
		}) ?? []
	).filter((c) => !isApplicantStructureLocked && !(c.isUnique && c.hasConnections));

	const getEntityLabel = useGetEntityLabel();

	return (
		<Box pos="relative">
			<Box
				sx={({ colors }) => ({
					backgroundColor: '#fff',
					borderRadius: '12px',
					padding: '12px',
					boxShadow: '0px 4px 20px -4px rgba(0, 0, 0, 0.10)',
					minWidth: '200px',
					width: isEditable ? '300px' : '250px',
					border: `1px solid ${hasErrors ? colors.red[6] : 'transparent'}`,
				})}
			>
				<Stack gap="20px">
					<Flex align="center">
						<Flex
							justify="center"
							align="center"
							w="36px"
							h="36px"
							miw="36px"
							bg="#F3F4F6"
							sx={{ borderRadius: '50%' }}
						>
							{type === 'person' ? <Icon.IconUser size={24} /> : <Icon.IconBuildingSkyscraper size={24} />}
						</Flex>
						<Stack gap="8px" ml="12px">
							<Flex justify="space-between">
								<TFText fz="16px" fw="500" lh="20px">
									{data.label}
								</TFText>
								{data.connectionKind === 'UBO' && (
									<Badge>
										{_(
											msg({
												id: 'structure.accountStructure.entityNode.uboBadge',
												message: 'UBO',
											}),
										)}
									</Badge>
								)}
							</Flex>
							<EntityBadge kind={getEntityLabel(data.kind)} />
							{hasErrors && isEditable && (
								<TFText c="red.6" fz="14px" lh="18px" fw="500">
									{_(
										msg({
											id: 'structure.accountStructure.entityNode.errorText',
											message: `Please add all required information`,
										}),
									)}
								</TFText>
							)}
						</Stack>
					</Flex>
					{isEditable && (
						<Flex gap="8px">
							{(!isIndividual || isMainEntityOrAccount) && Boolean(connectionOptions.length) && (
								<Menu
									opened={connectionsMenu?.params?.entityId === data.graphId}
									withinPortal
									offset={0}
									withArrow={false}
									position="bottom-start"
								>
									<Menu.Target>
										<Button
											flex={2}
											ref={connectionsMenuRef}
											p="0 8px"
											leftSection={<Icon.IconPlus size="18" />}
											sx={{ borderRadius: '8px' }}
											styles={{ section: { marginRight: '4px' } }}
											h="34px"
											fullWidth={isMainEntityOrAccount}
											variant="light"
											onClick={(e) => {
												e.stopPropagation();
												connectionsMenu?.open({ entityId: data.graphId });
											}}
										>
											{_(
												msg({
													id: 'structure.accountStructure.entityNode.addConnectionButton.label',
													message: `Add connection`,
												}),
											)}
										</Button>
									</Menu.Target>

									<Menu.Dropdown>
										{connectionOptions.map(({ label, kind }) => (
											<Menu.Item
												key={kind}
												p="12px"
												sx={() => ({
													'&:hover': { backgroundColor: '#EFF6FF' },
												})}
												leftSection={<Icon.IconPlus size={18} />}
												onClick={() =>
													addConnectionModal?.open({
														connectionKind: kind,
														parentEntityKind: data.kind,
														parentEntityName: data.label,
														parentEntityId: data.graphId,
														withSuccessMessage: false,
													})
												}
											>
												<TFText fz="14px" fw="500">
													{label}
												</TFText>
											</Menu.Item>
										))}
									</Menu.Dropdown>
								</Menu>
							)}
							{editContext && (
								<Button
									flex={1}
									fullWidth={isIndividual}
									p="0 8px"
									leftSection={<Icon.IconPencil size="18" />}
									sx={{ borderRadius: '8px' }}
									styles={{ section: { marginRight: '4px' } }}
									h="34px"
									variant="light"
									onClick={() => formModal?.open(editContext)}
								>
									{_(
										msg({
											id: 'structure.accountStructure.entityNode.editConnectionButton.label',
											message: `Edit`,
										}),
									)}
								</Button>
							)}
						</Flex>
					)}
				</Stack>
			</Box>

			{/* Required to draw edges */}
			<Handle type="source" style={{ display: 'none' }} position={Position.Bottom} isConnectable />
			<Handle type="target" style={{ display: 'none' }} position={Position.Top} isConnectable />
		</Box>
	);
};

export const nodeTypes: Record<StructureNodeType, React.FC<any>> = {
	account: EntityNode,
	person: EntityNode,
	company: EntityNode,
};

export const EntityEdge: React.FC<EdgeProps<StructureEdgeData>> = ({ id, data, source, target, markerEnd, style }) => {
	const sourceNode = useStore<any>(useCallback((store) => store.nodeInternals.get(source), [source]));
	const targetNode = useStore<any>(useCallback((store) => store.nodeInternals.get(target), [target]));

	const { removeConnectionModal } = useModalsController() ?? {};

	const edgesBetweenSameNodes: Edge[] = useStore<any>((s) => {
		return s.edges.filter((e) => {
			return (e.source === source && e.target === target) || (e.source === target && e.target === source);
		});
	});
	const thisEdgeIndex = edgesBetweenSameNodes.findIndex((e) => e.id === id);

	if (!sourceNode || !targetNode) {
		return null;
	}

	const { sx, sy, tx, ty, sourcePos, targetPos } = getEdgeParams(sourceNode, targetNode);

	const [edgePath, labelX, labelY] = getBezierPath({
		sourceX: sx,
		sourceY: sy,
		sourcePosition: sourcePos,
		targetPosition: targetPos,
		targetX: tx,
		targetY: ty,
	});

	const labelStepPx = 45;
	const labelYInitial = labelY - (labelStepPx * (edgesBetweenSameNodes.length - 1)) / 2;

	return (
		<>
			<path id={id} className="react-flow__edge-path" d={edgePath} markerEnd={markerEnd} style={style} />
			<EdgeLabelRenderer>
				<Box
					sx={() => ({
						position: 'absolute',
						transform: `translate(-50%, -50%) translate(${labelX}px,${labelYInitial + labelStepPx * thisEdgeIndex}px)`,
						color: '#6B7280',
						padding: '8px',
						fontSize: '14px',
						fontWeight: 500,
						lineHeight: '21px',
						pointerEvents: 'all',
						boxShadow: '0px 4px 6px 0px rgba(0, 0, 0, 0.09)',
						backgroundColor: 'white',
						borderRadius: '8px',
					})}
					className="nodrag nopan"
				>
					{data?.label ?? 'N/A'}
					{removeConnectionModal && (
						<ActionIcon
							size={20}
							ml="4px"
							sx={({ colors }) => ({
								'&:hover': { backgroundColor: colors.slate[1] },
							})}
							className="edgebutton"
							onClick={() => {
								const { parentEntityName, label, parentEntityId, childNodeId, connectionKind } = data ?? {};
								if (
									!parentEntityId ||
									!parentEntityName ||
									!parentEntityId ||
									!childNodeId ||
									!connectionKind ||
									!label
								)
									return;
								removeConnectionModal?.open({
									parentEntityName,
									parentEntityId,
									entityId: childNodeId,
									connectionKind,
								});
							}}
							variant="transparent"
							color="#6B7280"
						>
							<Icon.IconX size="12" />
						</ActionIcon>
					)}
				</Box>
			</EdgeLabelRenderer>
		</>
	);
};

const edgeTypes = {
	floating: EntityEdge,
};
